<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <!-- Message de confirmation -->
      <div v-if="confirmationMessage" class="alert alert-success alert-dismissible fade show" role="alert">
        <i class="fa fa-check-circle me-1"></i>
        {{ confirmationMessage }}
        <button type="button" class="btn-close" @click="confirmationMessage = null" aria-label="Close"></button>
      </div>

      <!-- État de chargement -->
      <div v-if="isLoading" class="text-center">
        <p class="mb-10">Chargement des questions...</p>
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>

      <!-- Affichage des résultats -->
      <div v-else-if="showResults" class="results-container">
        <div class="">
          <center>
            <h3 class="mb-20">Test Terminé !</h3>
            <p v-if="sessionId">Session ID: {{ sessionId }}</p>
            <p v-if="sessionCode">Code de la session: {{ sessionCode }}</p>
          </center>
          <div class="row gx-2">
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Score</h4>
                <p class="fs-1 fw-bold text-success">{{ testResults.score }} / {{ testResults.total }}</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Pourcentage</h4>
                <p class="fs-1 fw-bold" :class="testResults.percentage >= 50 ? 'text-success' : 'text-danger'">
                  {{ testResults.percentage }}%
                </p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Bonnes réponses</h4>
                <p class="fs-1 fw-bold text-success">{{ testResults.correctAnswers }}</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Mauvaises réponses</h4>
                <p class="fs-1 fw-bold text-danger">{{ testResults.incorrectAnswers }}</p>
              </div>
            </div>
          </div>
          
          <!-- Affichage détaillé des 20 questions avec réponses -->
          <div class="text-start mt-10">
            <h4 class="mb-15 text-center">📋 Revue détaillée de vos réponses</h4>
            <div class="questions-review">
              <div 
                v-for="(question, index) in questionsWithAnswers" 
                :key="question.id"
                class="question-review-item mb-15 border"
                :class="question.isCorrect ? 'border-success' : 'border-danger'"
              >
                <div class="question-header p-5" :class="question.isCorrect ? 'bg-success-subtle' : 'bg-danger-subtle'">
                  <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0 fw-bold">
                      <span class="question-number">Question {{ index + 1 }}</span>
                    </h5>
                    <span class="fs-3" :class="question.isCorrect ? 'text-success' : 'text-danger'">
                      {{ question.isCorrect ? '✓' : '✗' }}
                    </span>
                  </div>
                </div>
                
                <div class="question-content p-10 bg-white">
                  <p class="fw-semibold mb-10 text-black">{{ question.contenu }}</p>
                  
                  <!-- Réponse de l'utilisateur -->
                  <div class="user-answer mb-5 p-10 rounded" :class="question.isCorrect ? 'bg-light' : 'bg-light'">
                    <strong class="text-black">Votre réponse :</strong>
                    <span 
                      class="ms-2 fw-semibold"
                      :class="question.isCorrect ? 'text-success' : 'text-danger text-decoration-line-through'"
                    >
                      {{ question.userAnswerText }}
                    </span>
                  </div>
                  
                  <!-- Bonne réponse (affichée si la réponse est incorrecte) -->
                  <div v-if="!question.isCorrect" class="correct-answer-display p-10 bg-success-subtle rounded">
                    <strong class="text-blue">✓ Bonne réponse :</strong>
                    <span class="ms-2 text-blue fw-bold">
                      {{ question.correctAnswerText }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Afficher le formulaire uniquement si des questions sont disponibles -->
      <Form
        v-else-if="questions.length > 0"
        ref="testForm"
        @submit="handleSubmit"
        :validation-schema="testSchema"
      >
        <!-- Barre de progression -->
        <div class="progress mb-20" style="height: 8px;">
          <div 
            class="progress-bar bg-success" 
            :style="{ width: progressPercentage + '%' }"
          ></div>
        </div>

        <!-- Afficher la question actuelle -->
        <div v-if="currentQuestion" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Question {{ currentQuestionIndex + 1 }} / {{ questions.length }}
          </label>
          <div class="alert alert-info">
            <strong>{{ currentQuestion.contenu }}</strong>
          </div>
        </div>

        <!-- Choix multiple -->
        <div
          v-if="currentQuestion && currentQuestion.type === 'choix_multiple'"
          class="form-group mb-10"
        >
          <label class="d-block text-black fw-semibold mb-10">
            Sélectionner une réponse <span class="text-danger">*</span>
          </label>
          <div v-if="propositionOptions.length > 0" class="border border-primary p-10">
            <div v-for="(proposition, index) in propositionOptions" :key="`prop-${proposition.value}-${index}`">
              <div class="d-flex align-items-center mb-5">
                <Field
                  name="answer"
                  type="radio"
                  :value="proposition.value"
                  v-model="formData.answer"
                  :id="`proposition-${proposition.value}`"
                  class="form-check-input me-2"
                  @change="onAnswerChange"
                />
                <label
                  class="text-black"
                  :for="`proposition-${proposition.value}`"
                  style="cursor: pointer;"
                >
                  {{ proposition.label }}
                </label>
              </div>
            </div>
            <ErrorMessage name="answer" class="text-danger" />
          </div>
          <div v-else class="text-danger">
            Aucune proposition disponible pour cette question.
          </div>
        </div>

        <!-- Texte libre -->
        <div v-else-if="currentQuestion && currentQuestion.type === 'texte_libre'" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Votre réponse <span class="text-danger">*</span>
          </label>
          <Field
            name="answer"
            as="textarea"
            class="form-control shadow-none text-black"
            v-model="formData.answer"
            placeholder="Entrez votre réponse"
            rows="4"
            @input="onAnswerChange"
          />
          <ErrorMessage name="answer" class="text-danger" />
        </div>

        <!-- Oui/Non -->
        <div v-else-if="currentQuestion && currentQuestion.type === 'oui_non'" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Sélectionner une réponse <span class="text-danger">*</span>
          </label>
          <div class="border border-primary p-10">
            <div class="d-flex align-items-center mb-5">
              <Field
                name="answer"
                type="radio"
                value="Oui"
                v-model="formData.answer"
                id="oui"
                class="form-check-input me-2"
                @change="onAnswerChange"
              />
              <label class="text-black" for="oui" style="cursor: pointer;">Oui</label>
            </div>
            <div class="d-flex align-items-center mb-5">
              <Field
                name="answer"
                type="radio"
                value="Non"
                v-model="formData.answer"
                id="non"
                class="form-check-input me-2"
                @change="onAnswerChange"
              />
              <label class="text-black" for="non" style="cursor: pointer;">Non</label>
            </div>
            <ErrorMessage name="answer" class="text-danger" />
          </div>
        </div>

        <!-- Actions (boutons) -->
            
        <div class="d-flex align-items-center mt-10">
          <button
            type="button"
            class="btn btn-secondary me-3"
            @click="previousQuestion"
            :disabled="currentQuestionIndex === 0"
            :class="{ 'cursor-not-allowed': currentQuestionIndex === 0 }"
          >
            Précédent
          </button>

          <button
            type="submit"
            class="btn"
            :class="[
              isLastQuestion ? 'btn-success' : 'btn-info',
              { 'cursor-not-allowed': isDisable }
            ]"
            :disabled="isDisable || isSubmitting"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else :class="isLastQuestion ? 'fa fa-check me-1' : 'fa fa-arrow-right me-1'"></i>
            {{ isLastQuestion ? 'Terminer le test' : 'Suivant' }}
          </button>
        </div>
        <!-- Feedback utilisateur -->
        <div class="mt-10" v-if="isDisable">
          <small class="text-danger">
            
            <span v-if="!currentQuestion">Aucune question disponible</span>
           
          </small>
        </div>
        <div class="mt-10" v-else>
          <small class="text-success">
            <i class="fa fa-check-circle me-1"></i>
            Prêt à continuer
          </small>
        </div>
      </Form>

      <!-- Message si aucune question n'est disponible -->
      <div v-else class="text-danger text-center">
        <div class="alert alert-danger">
          <h4>Test impossible à générer</h4>
          <p class="mb-10">
            Le test ne peut pas être généré car il nécessite exactement <strong>20 questions</strong> pour être valide.
          </p>
          <div class="alert alert-warning text-start">
            <strong>Causes possibles :</strong>
            <ul class="mb-0">
              <li>Moins de 20 questions disponibles pour les critères sélectionnés (niveau, matière, filière)</li>
              <li>Questions invalides (sans propositions ou sans bonnes réponses)</li>
              <li>Paramètres de niveau, matière ou filière incorrects</li>
              <li>Problème de connexion à l'API</li>
            </ul>
          </div>
          <div class="alert alert-info text-start mt-10">
            <strong>Solutions :</strong>
            <ul class="mb-0">
              <li>Vérifiez qu'il y a au moins 20 questions valides dans la base de données</li>
              <li>Ajoutez des questions pour cette combinaison niveau/matière/filière</li>
              <li>Modifiez les critères de sélection</li>
            </ul>
          </div>
        </div>
        <div class="d-flex justify-content-center gap-2">
          <router-link to="/liste-questions" class="btn btn-primary">
            <i class="fa fa-list me-1"></i>
            Voir les questions
          </router-link>
          <router-link to="/add-question" class="btn btn-success">
            <i class="fa fa-plus me-1"></i>
            Ajouter des questions
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import { useRouter, useRoute } from 'vue-router';
import { success, error } from '@/utils/utils';
import ApiService from '@/services/ApiService';

// Interfaces
interface Proposition {
  id: number;
  contenu: string;
  estLaBonneReponse?: boolean;
}

interface Question {
  id: number;
  contenu: string;
  type: string;
  propositions?: Proposition[];
}

interface FormValues {
  answer: string | null;
}

interface TestResults {
  score: number;
  total: number;
  percentage: number;
  correctAnswers: number;
  incorrectAnswers: number;
}

interface QuestionWithAnswer {
  id: number;
  contenu: string;
  type: string;
  userAnswerText: string;
  correctAnswerText: string;
  isCorrect: boolean;
}

export default defineComponent({
  name: 'TestPage',
  components: {
    Form,
    Field,
    ErrorMessage,
  },
  setup() {
    // Dépendances
    const router = useRouter();
    const route = useRoute();
    const testForm = ref<InstanceType<typeof Form> | null>(null);

    // Données réactives
    const questions = ref<Question[]>([]);
    const currentQuestionIndex = ref(0);
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const sessionId = ref<number | null>(null);
    const sessionCode = ref<string | null>(null);
    const confirmationMessage = ref<string | null>(null); // Message de confirmation
    const showResults = ref(false);
    const testResults = reactive<TestResults>({
      score: 0,
      total: 0,
      percentage: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
    });
    const formData = reactive<FormValues>({
      answer: null,
    });
    const answers = ref<{ [key: number]: string }>({});

    // Schéma de validation
    const testSchema = Yup.object().shape({
      answer: Yup.string().required('Une réponse est obligatoire'),
    });

    // Propriétés calculées
    const currentQuestion = computed(() => {
      return questions.value[currentQuestionIndex.value] || null;
    });

    const propositionOptions = computed(() => {
      if (currentQuestion.value && currentQuestion.value.type === 'choix_multiple') {
        console.log('Propositions brutes:', currentQuestion.value.propositions);
        const options = currentQuestion.value.propositions?.map((prop) => ({
          value: String(prop.id),
          label: prop.contenu || 'Proposition inconnue',
        })) || [];
        console.log('PropositionOptions:', options);
        return options;
      }
      return [];
    });

    const isLastQuestion = computed(() => {
      return currentQuestionIndex.value === questions.value.length - 1;
    });

    const isDisable = computed(() => {
      return !currentQuestion.value || !formData.answer || formData.answer.trim() === '';
    });

    const progressPercentage = computed(() => {
      if (questions.value.length === 0) return 0;
      return Math.round(((currentQuestionIndex.value + 1) / questions.value.length) * 100);
    });

    const answeredCount = computed(() => {
      return Object.keys(answers.value).length;
    });

    // Propriété calculée pour afficher les questions avec réponses détaillées
    const questionsWithAnswers = computed((): QuestionWithAnswer[] => {
      return questions.value.map((question) => {
        const userAnswer = answers.value[question.id];
        let userAnswerText = 'Non répondu';
        let correctAnswerText = 'Non disponible';
        let isCorrect = false;

        // Trouver la bonne réponse
        const correctProposition = question.propositions?.find(p => p.estLaBonneReponse);
        if (correctProposition) {
          correctAnswerText = correctProposition.contenu;
        }

        // Traiter la réponse de l'utilisateur
        if (userAnswer) {
          if (question.type === 'choix_multiple' || question.type === 'oui_non') {
            const userProposition = question.propositions?.find(p => String(p.id) === userAnswer);
            userAnswerText = userProposition ? userProposition.contenu : userAnswer;
            isCorrect = userProposition?.estLaBonneReponse || false;
          } else if (question.type === 'texte_libre') {
            userAnswerText = userAnswer;
            isCorrect = correctProposition ? userAnswer.toLowerCase().trim() === correctProposition.contenu.toLowerCase().trim() : false;
          }
        }

        return {
          id: question.id,
          contenu: question.contenu,
          type: question.type,
          userAnswerText,
          correctAnswerText,
          isCorrect,
        };
      });
    });

    // Chargement des questions via API
    const loadQuestionsFromApi = async () => {
      const testData = {
        niveauId: Number(route.query.niveauId) || null,
        matiereId: Number(route.query.matiereId) || null,
        filiereId: route.query.filiereId ? Number(route.query.filiereId) : null,
        count: 20,
      };
      
      console.log('Données de test envoyées:', testData);
      
      // Validation des paramètres obligatoires
      if (!testData.niveauId || !testData.matiereId) {
        throw new Error('Les paramètres niveauId et matiereId sont obligatoires');
      }

      try {
        const { data } = await ApiService.post('/random-questions', testData);
        console.log('Réponse API complète:', data);
        
        if (data && data.code === 200 && data.data) {
          // Récupérer le sessionId et sessionCode
          if (data.data.sessionId) {
            sessionId.value = data.data.sessionId;
            console.log('Session ID récupéré:', sessionId.value);
          } else {
            console.warn('SessionId non trouvé dans la réponse API');
          }

          if (data.data.code) {
            sessionCode.value = data.data.code;
            console.log('Session Code récupéré:', sessionCode.value);
            localStorage.setItem('testSessionCode', data.data.code);
            // Définir le message de confirmation
            confirmationMessage.value = `Session créée avec succès ! Code : ${data.data.code}`;
            // Faire disparaître le message après 5 secondes
            setTimeout(() => {
              confirmationMessage.value = null;
            }, 5000);
          } else {
            console.warn('SessionCode non trouvé dans la réponse API');
          }
          
          // Récupérer les questions
          const questionsArray = data.data.questions || data.data;
          
          if (!Array.isArray(questionsArray)) {
            throw new Error('Format de réponse invalide : questions n\'est pas un tableau');
          }
          
          // Contrôle strict : vérifier qu'on a exactement 20 questions
          if (questionsArray.length < 20) {
            throw new Error(`Test impossible : seulement ${questionsArray.length} question(s) disponible(s) sur les 20 requises. Un test nécessite exactement 20 questions pour être valide.`);
          }
          
          questions.value = questionsArray.slice(0, 20);
          console.log('Questions chargées via API:', questions.value.length);
          console.log('Session ID stocké:', sessionId.value);
          console.log('Session Code stocké:', sessionCode.value);
          
          // Stocker dans localStorage
          localStorage.setItem('testQuestions', JSON.stringify(questions.value));
          localStorage.setItem('testSessionId', String(sessionId.value));
          return true;
        } else {
          console.error('Format de réponse API invalide:', data);
          throw new Error('Aucune question disponible pour ces critères');
        }
      } catch (err: any) {
        console.error('Erreur détaillée API:', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
          config: err.config
        });
        throw err;
      }
    };

    // Récupérer les résultats du test
    const fetchTestResults = async () => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible de récupérer les résultats');
        return;
      }

      try {
        console.log('Récupération des résultats pour la session:', sessionId.value);
        const { data } = await ApiService.get(`/sessions/${sessionId.value}/results`);
        
        console.log('Résultats reçus:', data);
        
        if (data && data.code === 200 && data.data) {
          testResults.score = data.data.score || 0;
          testResults.total = data.data.total || questions.value.length;
          testResults.percentage = data.data.percentage || 0;
          testResults.correctAnswers = data.data.correctAnswers || 0;
          testResults.incorrectAnswers = data.data.incorrectAnswers || 0;
          
          console.log('Résultats du test:', testResults);
        } else {
          throw new Error('Format de réponse invalide pour les résultats');
        }
      } catch (err: any) {
        console.error('Erreur lors de la récupération des résultats:', err);
        error(`Impossible de récupérer les résultats: ${err.message || 'Erreur inconnue'}`);
      }
    };

    // Enregistrer les résultats du test
    const saveTestResults = async () => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible d\'enregistrer les résultats');
        return false;
      }

      try {
        const formattedAnswers = Object.entries(answers.value).map(([questionId, answer]) => {
          const question = questions.value.find(q => q.id === Number(questionId));
          if (!question) {
            console.error(`Question non trouvée pour ID ${questionId}`);
            return null;
          }
          if (question.type === 'choix_multiple') {
            return {
              questionId: Number(questionId),
              propositionId: Number(answer),
              contenu: null,
            };
          } else if (question.type === 'oui_non') {
            const proposition = question.propositions?.find(p => p.contenu === answer);
            return {
              questionId: Number(questionId),
              propositionId: proposition ? proposition.id : null,
              contenu: answer,
            };
          } else {
            return {
              questionId: Number(questionId),
              propositionId: null,
              contenu: answer,
            };
          }
        }).filter(answer => answer !== null);

        const submissionData = {
          attempt: 0, // Session originale
          score: testResults.score,
          total: testResults.total,
          percentage: testResults.percentage,
          correctAnswers: testResults.correctAnswers,
          incorrectAnswers: testResults.incorrectAnswers,
          answers: formattedAnswers,
        };

        console.log('Envoi des résultats à l\'API:', submissionData);

        const response = await ApiService.post(`/sessions/${sessionId.value}/results`, submissionData);

        console.log('Réponse API enregistrement des résultats:', response);

        if (response.data && (response.data.code === 201 || response.data.code === 200)) {
          console.log('✓ Résultats et réponses enregistrés avec succès');
          return true;
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'enregistrement des résultats');
        }
      } catch (err: any) {
        console.error('Erreur lors de l\'enregistrement des résultats:', err);
        error(`Erreur lors de l'enregistrement des résultats: ${err.message || 'Erreur inconnue'}`);
        return false;
      }
    };

    // Chargement des questions
    onMounted(async () => {
      console.log('=== DÉBUT CHARGEMENT DES QUESTIONS ===');
      console.log('Paramètres de route:', route.query);
      
      try {
        isLoading.value = true;
        
        // Essayer de récupérer depuis localStorage d'abord
        const storedQuestions = localStorage.getItem('testQuestions');
        const storedSessionId = localStorage.getItem('testSessionId');
        const storedSessionCode = localStorage.getItem('testSessionCode');
        console.log('Questions stockées dans localStorage:', !!storedQuestions);
        console.log('SessionId stocké dans localStorage:', storedSessionId);
        console.log('SessionCode stocké dans localStorage:', storedSessionCode);
        
        if (storedQuestions && storedSessionId) {
          try {
            const parsedQuestions = JSON.parse(storedQuestions);
            if (Array.isArray(parsedQuestions) && parsedQuestions.length >= 20) {
              questions.value = parsedQuestions.slice(0, 20);
              sessionId.value = Number(storedSessionId);
              sessionCode.value = storedSessionCode;
              console.log('Questions chargées depuis localStorage:', questions.value.length);
              console.log('Session ID récupéré depuis localStorage:', sessionId.value);
              console.log('Session Code récupéré depuis localStorage:', sessionCode.value);
              // Afficher le message de confirmation depuis localStorage
              if (storedSessionCode) {
                confirmationMessage.value = `Session chargée avec succès ! Code : ${storedSessionCode}`;
                setTimeout(() => {
                  confirmationMessage.value = null;
                }, 5000);
              }
              localStorage.removeItem('testQuestions');
              localStorage.removeItem('testSessionId');
              localStorage.removeItem('testSessionCode');
              return;
            } else if (Array.isArray(parsedQuestions) && parsedQuestions.length > 0) {
              console.warn(`localStorage contient seulement ${parsedQuestions.length} question(s), 20 requises`);
            }
          } catch (parseError) {
            console.error('Erreur parsing localStorage:', parseError);
          }
        }

        // Fallback: charger via API
        console.log('Chargement via API...');
        await loadQuestionsFromApi();
        
      } catch (err: any) {
        console.error('Erreur lors du chargement:', err);
        error(`Erreur lors du chargement des questions: ${err.message || 'Erreur inconnue'}`);
      } finally {
        isLoading.value = false;
        console.log('=== FIN CHARGEMENT - Questions disponibles:', questions.value.length);
        console.log('Session ID final:', sessionId.value);
        console.log('Session Code final:', sessionCode.value);
      }
    });

    // Gestion du changement de réponse
    const onAnswerChange = () => {
      console.log('Réponse sélectionnée:', formData.answer);
      if (testForm.value) {
        testForm.value.setFieldValue('answer', formData.answer);
        testForm.value.validateField('answer');
      }
      // Effacer le message de confirmation lorsque l'utilisateur commence à répondre
      confirmationMessage.value = null;
    };

    // Fonction pour enregistrer une réponse individuelle
    const saveAnswer = async (questionId: number, answer: string) => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible d\'enregistrer la réponse');
        return false;
      }

      const question = questions.value.find(q => q.id === questionId);
      if (!question) {
        console.error('Question non trouvée');
        return false;
      }

      console.log(`Enregistrement de la réponse pour la question ${questionId}:`, { type: question.type, answer });

      // Formater la réponse selon le type de question
      let formattedAnswer;
      if (question.type === 'choix_multiple') {
        formattedAnswer = {
          questionId: questionId,
          propositionId: Number(answer),
          contenu: null
        };
      } else if (question.type === 'oui_non') {
        const proposition = question.propositions?.find(p => p.contenu === answer);
        formattedAnswer = {
          questionId: questionId,
          propositionId: proposition ? proposition.id : null,
          contenu: answer
        };
      } else {
        formattedAnswer = {
          questionId: questionId,
          propositionId: null,
          contenu: answer
        };
      }

      try {
        // Enregistrer la réponse via API
        const submissionData = {
          sessionId: sessionId.value,
          answers: [formattedAnswer]
        };

        console.log('Envoi de la réponse à l\'API:', submissionData);

        const response = await ApiService.post('/reponses', submissionData);

        console.log('Réponse API enregistrement:', response);

        if (response.data && (response.data.code === 201 || response.data.code === 200)) {
          console.log(`✓ Réponse enregistrée avec succès pour la question ${questionId}`);
          return true;
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'enregistrement');
        }
      } catch (err: any) {
        console.error('Erreur lors de l\'enregistrement de la réponse:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Erreur inconnue';
        error(`Erreur lors de l'enregistrement: ${errorMessage}`);
        return false;
      }
    };

    // Navigation vers la question précédente
    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        // Sauvegarder la réponse actuelle en local (sans appel API)
        if (currentQuestion.value && formData.answer) {
          answers.value[currentQuestion.value.id] = formData.answer;
        }
        
        // Aller à la question précédente
        currentQuestionIndex.value--;
        
        // Charger la réponse sauvegardée
        const savedAnswer = answers.value[currentQuestion.value?.id || 0];
        formData.answer = savedAnswer || null;
        
        // Réinitialiser le formulaire avec la nouvelle valeur
        if (testForm.value) {
          testForm.value.resetForm({ values: { answer: formData.answer } });
        }
        
        onAnswerChange();
      }
    };

    // Gestion de la soumission
    const handleSubmit = async (values: FormValues) => {
      console.log('=== SOUMISSION ===');
      console.log('Valeurs soumises:', values);
      console.log('Question actuelle:', currentQuestion.value);
      
      if (!currentQuestion.value) {
        error('Aucune question disponible.');
        return;
      }

      try {
        isSubmitting.value = true;

        // Sauvegarder la réponse actuelle en local
        answers.value[currentQuestion.value.id] = values.answer || '';
        console.log('Réponses sauvegardées:', answers.value);

        if (isLastQuestion.value) {
          // Calculer les résultats localement avant d'enregistrer
          let correctAnswers = 0;
          questions.value.forEach((question) => {
            const userAnswer = answers.value[question.id];
            if (userAnswer) {
              const correctProposition = question.propositions?.find(p => p.estLaBonneReponse);
              if (correctProposition) {
                if (question.type === 'choix_multiple' || question.type === 'oui_non') {
                  if (String(correctProposition.id) === userAnswer) {
                    correctAnswers++;
                  }
                } else if (question.type === 'texte_libre') {
                  if (userAnswer.toLowerCase().trim() === correctProposition.contenu.toLowerCase().trim()) {
                    correctAnswers++;
                  }
                }
              }
            }
          });

          testResults.score = correctAnswers;
          testResults.total = questions.value.length;
          testResults.percentage = Math.round((correctAnswers / questions.value.length) * 100);
          testResults.correctAnswers = correctAnswers;
          testResults.incorrectAnswers = questions.value.length - correctAnswers;

          // Enregistrer les résultats via API
          const resultsSaved = await saveTestResults();
          if (!resultsSaved) {
            error('Impossible d\'enregistrer les résultats. Veuillez réessayer.');
            return;
          }

          // Récupérer les résultats pour confirmation
         // await fetchTestResults();
          
          success('Test terminé avec succès ! Toutes vos réponses et résultats ont été enregistrés.');
          
          // Afficher les résultats
          showResults.value = true;
          
          // Nettoyer localStorage
          localStorage.removeItem('testQuestions');
          localStorage.removeItem('testSessionId');
          localStorage.removeItem('testSessionCode');
        } else {
          // Passer à la question suivante
          currentQuestionIndex.value++;
          
          // Charger la réponse sauvegardée pour la nouvelle question (si elle existe)
          const savedAnswer = answers.value[currentQuestion.value?.id || 0];
          formData.answer = savedAnswer || null;
          
          // Réinitialiser le formulaire
          if (testForm.value) {
            testForm.value.resetForm({ values: { answer: formData.answer } });
          }
          
          onAnswerChange();
        }
      } catch (err: any) {
        console.error('Erreur lors de la soumission:', err);
        error('Une erreur est survenue. Veuillez réessayer.');
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      testForm,
      questions,
      currentQuestionIndex,
      currentQuestion,
      formData,
      testSchema,
      propositionOptions,
      isLastQuestion,
      isDisable,
      isLoading,
      isSubmitting,
      progressPercentage,
      answeredCount,
      sessionId,
      sessionCode,
      confirmationMessage, // Retourner confirmationMessage
      showResults,
      testResults,
      questionsWithAnswers,
      previousQuestion,
      handleSubmit,
      onAnswerChange,
    };
  },
});
</script>

<style scoped>
/* Général */
.card {
  border-radius: 0;
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.results-container {
  background-color: transparent !important;
}

.form-control,
.form-check-input {
  border-radius: 0;
  border-color: #ced4da;
}

.text-black {
  color: #212529; /* Teinte légèrement plus douce que #000 */
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
  font-weight: 500;
}

.text-success {
  color: #198754;
  font-weight: 500;
}

.text-blue {
  color: #0d6efd; /* Bleu primaire pour les bonnes réponses */
  font-weight: 500;
}

.border-primary {
  border-color: #0d6efd !important;
  border-width: 2px;
}

.bg-primary {
  background-color: #0d6efd !important;
}

/* Boutons */
.btn-success {
  background-color: #198754;
  border-color: #198754;
}

.btn-info {
  background-color: #0dcaf0;
  border-color: #0dcaf0;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn:hover {
  filter: brightness(90%);
  transition: all 0.2s ease;
}

.cursor-not-allowed {
  cursor: not-allowed;
  opacity: 0.7;
}

/* Espacements */
.p-5 {
  padding: 5px;
}

.p-10 {
  padding: 10px;
}

.p-15 {
  padding: 15px;
}

.mb-5 {
  margin-bottom: 5px;
}

.mb-10 {
  margin-bottom: 10px;
}

.mb-15 {
  margin-bottom: 15px;
}

.mb-20 {
  margin-bottom: 20px;
}

.mt-10 {
  margin-top: 10px;
}

.mt-20 {
  margin-top: 20px;
}

.fs-4 {
  font-size: 1.5rem;
  font-weight: 600;
}

/* Barre de progression */
.progress {
  background-color: #e9ecef;
  border-radius: 0;
  height: 8px;
}

.progress-bar {
  background-color: #198754;
  transition: width 0.3s ease;
  font-weight: 500;
}

.progress-bar.bg-danger {
  background-color: #dc3545 !important;
}

/* Alertes */
.alert {
  border-radius: 0;
  border: 1px solid;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
  border-color: #0d6efd;
  border-right-color: transparent;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Styles pour l'affichage des résultats détaillés */
.questions-review {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}

.question-review-item {
  transition: all 0.3s ease;
  border-width: 2px;
  border-radius: 4px;
}

.question-review-item.border-success {
  border-color: #198754;
}

.question-review-item.border-danger {
  border-color: #dc3545;
}

.question-review-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.question-header {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
}

.question-number {
  font-size: 1rem; /* Réduit la taille des numéros de questions */
}

.bg-success-subtle {
  background-color: #e6f4ea !important; /* Vert plus doux */
}

.bg-danger-subtle {
  background-color: #f8d7da !important; /* Rouge plus doux */
}

.text-decoration-line-through {
  text-decoration: line-through;
  text-decoration-color: #dc3545;
  text-decoration-thickness: 2px; /* Barré plus épais pour les réponses fausses */
  color: #dc3545 !important;
  font-weight: 600;
}

.user-answer {
  border-left: 4px solid #6c757d;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.user-answer .text-success {
  color: #198754 !important;
}

.correct-answer-display {
  border-left: 4px solid #0d6efd; /* Bordure bleue pour les bonnes réponses */
  background-color: #e7f1ff; /* Fond bleu clair */
  border-radius: 4px;
}

/* Badges */
.badge.bg-success {
  background-color: #198754 !important;
  color: #ffffff;
}

.badge.bg-danger {
  background-color: #dc3545 !important;
  color: #ffffff;
}

.badge.bg-secondary {
  background-color: #6c757d !important;
  color: #ffffff;
}
</style>